import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  PARTNER_CATEGORIES,
  PARTNER_CATEGORY_LABELS,
  PartnerCategory,
} from '../../constants/partnerNetwork';
import { registerPartner } from '../../services/partnerPortal';
import { Alert, inputClass } from '../partner-network/ui';

const emptyForm = {
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  website: '',
  linkedinUrl: '',
  category: '' as PartnerCategory | '',
  password: '',
  confirmPassword: '',
  agreeToTerms: false,
};

/**
 * Public partner application.
 *
 * Registration creates a pending account — approval is a separate decision at
 * Nexasolutra's end, so the success screen is explicit that they cannot sign in
 * yet and will be emailed.
 */
const PartnerRegister: React.FC = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const set = <K extends keyof typeof emptyForm>(
    key: K,
    value: (typeof emptyForm)[K]
  ) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.fullName.trim()) {
      setError('Your full name is required.');
      return;
    }
    if (!form.email.trim()) {
      setError('An email address is required — it becomes your sign-in.');
      return;
    }
    if (form.password.length < 8) {
      setError('Your password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    if (!form.agreeToTerms) {
      setError('Please accept the partner terms to continue.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await registerPartner({
        ...form,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
      });
      setDone(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not complete your registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="flex min-h-screen items-center justify-center bg-surface px-4 py-20">
        <AnimatedSection animation="slide-up" className="w-full max-w-lg">
          <div className="rounded-2xl border border-surface-border bg-surface-card p-8 text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-primary-500/40 bg-primary-500/10">
              <svg className="h-7 w-7 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mb-3 font-display text-2xl font-semibold text-white">
              Application received
            </h1>
            <p className="mb-8 text-sm text-muted">{done}</p>
            <Link to="/" className="btn-secondary py-2.5 px-5">
              Back to nexasolutra.com
            </Link>
          </div>
        </AnimatedSection>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-surface px-4 py-14">
      <AnimatedSection animation="slide-up" className="mx-auto w-full max-w-2xl">
        <div className="rounded-2xl border border-surface-border bg-surface-card p-8">
          <img src="/nexalogo.png" alt="Nexasolutra" className="mb-6 h-11 w-11 object-contain" />

          <h1 className="mb-2 font-display text-2xl font-semibold text-white">
            Become a Nexasolutra partner
          </h1>
          <p className="mb-8 text-sm text-muted">
            Refer hospitality and commerce businesses to us and earn commission on every
            project that closes. Applications are reviewed before your portal opens.
          </p>

          {error && (
            <Alert tone="error" className="mb-6">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm text-muted">Full name *</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => set('fullName', e.target.value)}
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Company</label>
                <input
                  type="text"
                  value={form.companyName}
                  onChange={(e) => set('companyName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">
                  What kind of partner are you?
                </label>
                <select
                  value={form.category}
                  onChange={(e) => set('category', e.target.value as PartnerCategory | '')}
                  className={inputClass}
                >
                  <option value="">Select one</option>
                  {PARTNER_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {PARTNER_CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  autoComplete="username"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Country</label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="https://"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">LinkedIn</label>
                <input
                  type="url"
                  value={form.linkedinUrl}
                  onChange={(e) => set('linkedinUrl', e.target.value)}
                  placeholder="https://linkedin.com/in/…"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Password *</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Confirm password *</label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => set('confirmPassword', e.target.value)}
                  autoComplete="new-password"
                  className={inputClass}
                />
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-muted">
              <input
                type="checkbox"
                checked={form.agreeToTerms}
                onChange={(e) => set('agreeToTerms', e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#16A34A]"
              />
              <span>
                I agree to the Nexasolutra partner terms: I will introduce businesses in good
                faith, and commission is earned on referrals that close, at the rate agreed
                with Nexasolutra.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Apply to join'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-muted">
            Already a partner?{' '}
            <Link to="/partner/login" className="text-primary-400 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </AnimatedSection>
    </section>
  );
};

export default PartnerRegister;
